import { useEffect, useRef, useState, type ReactNode } from "react";
import { Check, ChevronDown } from "lucide-react";
import clsx from "clsx";

export interface PillMenuOption {
  value: string;
  label: string;
  /** Shown before the label, in the menu and (for the current value) the pill. */
  lead?: ReactNode;
  hint?: string;
}

interface PillMenuProps {
  value: string;
  options: PillMenuOption[];
  onChange: (value: string) => void;
  /** Accessible name and the menu's heading: "Theme", "Language". */
  label: string;
  /** Pill icon; the current option's `lead` is used when not given. */
  icon?: ReactNode;
  /** Short text in the pill on phones, where the full label doesn't fit. */
  shortLabel?: string;
}

/**
 * A compact pill that opens a single-choice menu: the topbar's and login
 * screen's theme and language pickers. Keyboard: arrows move, Enter picks,
 * Escape closes and returns focus to the pill.
 */
export function PillMenu({
  value,
  options,
  onChange,
  label,
  icon,
  shortLabel,
}: PillMenuProps) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const itemRefs = useRef<(HTMLButtonElement | null)[]>([]);
  const current = options.find((o) => o.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return;
    // Focus the chosen item, so the arrows start from it.
    const i = Math.max(0, options.findIndex((o) => o.value === value));
    itemRefs.current[i]?.focus();
    const onDown = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open, options, value]);

  const close = () => {
    setOpen(false);
    triggerRef.current?.focus();
  };

  const onMenuKey = (e: React.KeyboardEvent) => {
    const items = itemRefs.current.filter(Boolean) as HTMLButtonElement[];
    const i = items.indexOf(document.activeElement as HTMLButtonElement);
    if (e.key === "Escape" || e.key === "Tab") {
      if (e.key === "Escape") e.preventDefault();
      close();
    } else if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      const step = e.key === "ArrowDown" ? 1 : -1;
      items[(i + step + items.length) % items.length]?.focus();
    } else if (e.key === "Home" || e.key === "End") {
      e.preventDefault();
      items[e.key === "Home" ? 0 : items.length - 1]?.focus();
    }
  };

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" && !open) {
            e.preventDefault();
            setOpen(true);
          }
        }}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={`${label}: ${current?.label ?? ""}`}
        title={label}
        className={clsx(
          "inline-flex h-9 items-center gap-2 rounded-full border pl-2.5 pr-2 text-sm font-medium transition-colors",
          "text-[rgb(var(--text-primary))]",
          "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[rgb(var(--text-accent))]",
          open
            ? "border-[rgb(var(--text-accent))]/40 bg-[rgb(var(--card-muted))]"
            : "border-[rgb(var(--border))] bg-[rgb(var(--card))] hover:bg-[rgb(var(--card-muted))]",
        )}
      >
        <span className="flex shrink-0 items-center text-[rgb(var(--icon-primary))]">
          {icon ?? current?.lead}
        </span>
        <span className="hidden max-w-[7.5rem] truncate sm:inline">
          {current?.label}
        </span>
        {shortLabel && <span className="sm:hidden">{shortLabel}</span>}
        <ChevronDown
          size={14}
          className={clsx(
            "shrink-0 text-[rgb(var(--text-muted))] transition-transform duration-200",
            open && "rotate-180",
          )}
        />
      </button>

      {open && (
        <div
          role="menu"
          aria-label={label}
          onKeyDown={onMenuKey}
          className="absolute right-0 top-full z-50 mt-2 w-56 origin-top-right animate-[pillMenuIn_140ms_ease-out] overflow-hidden rounded-2xl border border-[rgb(var(--border))] bg-[rgb(var(--card))] p-1.5 shadow-2xl shadow-slate-900/15"
        >
          <p className="px-2.5 pb-1 pt-1.5 text-[11px] font-semibold uppercase tracking-wider text-[rgb(var(--text-muted))]">
            {label}
          </p>
          {options.map((o, i) => {
            const selected = o.value === value;
            return (
              <button
                key={o.value}
                ref={(el) => {
                  itemRefs.current[i] = el;
                }}
                type="button"
                role="menuitemradio"
                aria-checked={selected}
                onClick={() => {
                  onChange(o.value);
                  close();
                }}
                className={clsx(
                  "flex w-full items-center gap-3 rounded-xl px-2.5 py-2 text-left text-sm transition-colors",
                  "hover:bg-[rgb(var(--card-muted))] focus-visible:bg-[rgb(var(--card-muted))] focus-visible:outline-none",
                  selected
                    ? "font-semibold text-[rgb(var(--text-primary))]"
                    : "text-[rgb(var(--text-secondary))]",
                )}
              >
                {o.lead && <span className="flex shrink-0">{o.lead}</span>}
                <span className="min-w-0 flex-1">
                  <span className="block truncate">{o.label}</span>
                  {o.hint && (
                    <span className="block truncate text-xs font-normal text-[rgb(var(--text-muted))]">
                      {o.hint}
                    </span>
                  )}
                </span>
                {selected && (
                  <Check
                    size={15}
                    className="shrink-0 text-[rgb(var(--text-accent))]"
                  />
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
